/**
 * Printable Receipt Component (Story 7.8, AC4)
 *
 * Text-based receipt layout optimized for ~80mm receipt paper.
 * Targeted by print CSS via id="redemption-receipt".
 */

import React from 'react';
import { useTranslation } from '@/i18n';
import { formatMoney, TimestampService } from '@zlinkx/shared-v2';
import type { CheckCouponCodeResponseType, CreateRedemptionResponseType } from '@zlinkx/shared-v2';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;

interface RedemptionReceiptProps {
	redemptionResult: CreateRedemptionResponseType;
	validatedCoupon: ValidatedCoupon;
	formContext: { invoiceId: string; note: string };
}

const RedemptionReceipt: React.FC<RedemptionReceiptProps> = ({
	redemptionResult,
	validatedCoupon,
	formContext,
}) => {
	const { t } = useTranslation();

	const getCouponTypeDisplay = (): string => {
		switch (validatedCoupon.couponType) {
			case 'percentage':
				return t('common.coupon.percentageOff', { value: validatedCoupon.discountValue ?? 0 });
			case 'fixed_amount':
				return t('common.coupon.amountOff', { value: (validatedCoupon.discountValue ?? 0) / 100 });
			case 'free_item':
			case 'bogo':
				return validatedCoupon.itemDescription ?? validatedCoupon.couponType;
			default:
				return validatedCoupon.couponType;
		}
	};

	const isFreeItemType = validatedCoupon.couponType === 'free_item' || validatedCoupon.couponType === 'bogo';
	const discountSign = t('merchant.redemption.discount.percentage', {
		amount: formatMoney(redemptionResult.data.discount_amount),
		percent: 0,
	}).charAt(0);

	return (
		<div id="redemption-receipt" className="font-mono text-sm max-w-xs mx-auto bg-white p-4">
			{/* Header */}
			<div className="text-center border-b border-dashed border-gray-400 pb-3 mb-3">
				<h3 className="text-base font-bold">{validatedCoupon.merchantName}</h3>
				<p className="text-xs text-gray-600 mt-1">
					{TimestampService.formatForDisplay(TimestampService.parseDateTimeInput(redemptionResult.timestamp))}
				</p>
			</div>

			{/* Transaction Details */}
			<div className="space-y-1.5 border-b border-dashed border-gray-400 pb-3 mb-3">
				<div className="flex justify-between">
					<span className="text-gray-600">{t('merchant.redemption.success.cashierLabel')}</span>
					<span>{redemptionResult.data.cashier_name}</span>
				</div>
				<div className="flex justify-between">
					<span className="text-gray-600">{t('merchant.redemption.success.customerLabel')}</span>
					<span>{validatedCoupon.customerName ?? t('merchant.redemption.success.guestCustomer')}</span>
				</div>
				{formContext.invoiceId && (
					<div className="flex justify-between">
						<span className="text-gray-600">{t('merchant.redemption.success.invoiceLabel')}</span>
						<span>{formContext.invoiceId}</span>
					</div>
				)}
			</div>

			{/* Coupon */}
			<div className="space-y-1.5 border-b border-dashed border-gray-400 pb-3 mb-3">
				<div className="flex justify-between">
					<span className="text-gray-600">{t('merchant.redemption.success.couponLabel')}</span>
					<span className="text-right max-w-[60%] truncate">{validatedCoupon.title}</span>
				</div>
				<div className="flex justify-between text-xs">
					<span className="text-gray-500">{t('merchant.redemption.success.couponTypeLabel')}</span>
					<span className="text-gray-500">{getCouponTypeDisplay()}</span>
				</div>
			</div>

			{/* Transaction Breakdown */}
			<div className="space-y-1.5 mb-3">
				<div className="flex justify-between">
					<span className="text-gray-600">{t('merchant.redemption.success.originalAmount')}</span>
					<span>{formatMoney(redemptionResult.data.original_amount)}</span>
				</div>
				<div className="flex justify-between text-green-700">
					<span>{t('merchant.redemption.success.discountAmount')}</span>
					<span>
						{discountSign}
						{formatMoney(redemptionResult.data.discount_amount)}
					</span>
				</div>

				{/* Free items */}
				{isFreeItemType && validatedCoupon.itemDescription && (
					<div className="flex justify-between text-green-700">
						<span>{t('merchant.redemption.success.freeItemsLabel')}</span>
						<span className="text-right max-w-[60%] truncate">{validatedCoupon.itemDescription}</span>
					</div>
				)}

				<div className="border-t border-gray-400 pt-1.5 flex justify-between font-bold">
					<span>{t('merchant.redemption.success.finalAmount')}</span>
					<span className="text-base">{formatMoney(redemptionResult.data.final_amount)}</span>
				</div>
			</div>

			{/* Footer */}
			<div className="text-center border-t border-dashed border-gray-400 pt-3 text-xs text-gray-500">
				<p>{t('merchant.redemption.success.poweredBy')}</p>
			</div>
		</div>
	);
};

export default RedemptionReceipt;
